/**
 * Magasin applicatif : relie l'interface au journal et au moteur de scrutin.
 *
 * Aucune règle métier ici : l'interface ne fait que lire l'état projeté et
 * ajouter des événements. Toute la logique vit dans `src/core`.
 */

import {
  CODE_LENGTHS,
  DEFAULT_RULES,
  compute,
  emptyState,
  generateCode,
  makeEvent,
  project,
  purgeDate,
  randomId,
  tokenHash,
  verifyChain,
  type Computation,
  type ElectionConfig,
  type ElectionState,
  type EventType,
  type JournalEvent,
  type Person,
  type Phase,
  type Seat,
} from '../core/index.js';
import {
  LocalTransport,
  listElections,
  loadCodes,
  originFor,
  purgeElection,
  rememberElection,
  saveCodes,
  type ElectionSummary,
  type StoredCodes,
} from '../transport/local.js';
import { creerElectionDistant, type EtatPublic } from '../transport/distant.js';
import { TransportHybride } from '../transport/hybride.js';
import type { Transport } from '../transport/transport.js';

export type Role = 'bureau' | 'isoloir' | 'affichage';

export type ThemeChoice = 'auto' | 'light' | 'dark';

const THEME_KEY = 'election.nsi.xyz/v1/theme';
const UNLOCK_KEY = 'election.nsi.xyz/v1/unlocked';
const SAUVEGARDE_KEY = 'election.nsi.xyz/v1/derniere-sauvegarde';
const LIAISON_KEY = 'election.nsi.xyz/v1/liaison';

/**
 * Liaison d'une élection avec le relais distant.
 *
 * On mémorise l'empreinte du code enseignant : c'est elle qui authentifie les
 * écritures auprès du relais, et elle doit être connue dès l'ouverture, avant
 * même que le journal local n'ait été projeté.
 */
export interface Liaison {
  readonly code: string;
  readonly teacherHash: string;
}

function lireLiaison(electionId: string): Liaison | null {
  try {
    const brut = localStorage.getItem(`${LIAISON_KEY}/${electionId}`);
    if (brut === null) return null;
    const parse = JSON.parse(brut) as Liaison;
    return typeof parse.code === 'string' && typeof parse.teacherHash === 'string' ? parse : null;
  } catch {
    return null;
  }
}

function ecrireLiaison(electionId: string, liaison: Liaison): void {
  localStorage.setItem(`${LIAISON_KEY}/${electionId}`, JSON.stringify(liaison));
}

function supprimerLiaison(electionId: string): void {
  localStorage.removeItem(`${LIAISON_KEY}/${electionId}`);
}

/**
 * Types d'événements qui changent ce que les élèves doivent voir.
 *
 * `ballot.cast` en est volontairement absent : publier à chaque bulletin
 * doublerait le journal sans rien apporter, l'isoloir n'affichant ni décompte
 * ni participation.
 */
const TYPES_PUBLICS: ReadonlySet<EventType> = new Set<EventType>([
  'phase.changed',
  'candidate.declared',
  'candidate.removed',
  'roster.imported',
  'codes.generated',
  'tour2.validated',
  'tiebreak.decided',
  'mandate.decided',
  'suppleant.designated',
  'result.validated',
  'result.overridden',
]);


/**
 * Sauvegarde complète : le journal **et** la planche de codes.
 *
 * Les codes ne figurent jamais dans le journal, seulement leurs empreintes.
 * Restaurer le seul journal sur un autre appareil donnerait donc une élection
 * où plus aucun code ne fonctionne. La sauvegarde les embarque, et l'interface
 * prévient qu'elle doit être conservée comme le code enseignant.
 */
export interface Sauvegarde {
  readonly format: 'election.nsi.xyz/sauvegarde';
  readonly version: 1;
  readonly exporteLe: number;
  readonly electionId: string;
  readonly journal: readonly JournalEvent[];
  readonly codes: StoredCodes | null;
}

/** État de préparation, pour donner un feu vert avant le jour J. */
export interface Diagnostic {
  readonly stockageOk: boolean;
  readonly evenements: number;
  readonly chaineOk: boolean;
  readonly chaineDetail: string | null;
  readonly codes: number;
  readonly eleves: number;
  readonly bulletins: number;
  readonly purgeLe: Date | null;
  readonly derniereSauvegarde: number | null;
}

class Store {
  private readonly listeners = new Set<() => void>();
  private detach: (() => void) | null = null;
  private file: Promise<unknown> = Promise.resolve();

  role: Role = 'bureau';
  origin = 'T_1';
  electionId: string | null = null;
  transport: Transport | null = null;
  /** Non nul lorsque l'élection est publiée sur le relais distant. */
  private hybride: TransportHybride | null = null;
  liaison: Liaison | null = null;
  events: JournalEvent[] = [];
  state: ElectionState = emptyState('');
  computation: Computation = compute(emptyState(''));
  theme: ThemeChoice = (localStorage.getItem(THEME_KEY) as ThemeChoice | null) ?? 'auto';

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }

  private recompute(): void {
    this.state = project(this.events, this.electionId ?? '');
    this.computation = compute(this.state);
  }

  summaries(): ElectionSummary[] {
    return listElections();
  }

  /**
   * Choisit le transport d'une élection : hybride si elle est publiée sur le
   * relais, local sinon. Le journal local reste l'autorité dans les deux cas.
   */
  private creerTransport(electionId: string): Transport {
    this.hybride?.arreter();
    this.hybride = null;
    const liaison = this.liaison;
    if (liaison !== null) {
      const hybride = new TransportHybride({
        local: new LocalTransport(electionId),
        electionId,
        secret: liaison.teacherHash,
      });
      this.hybride = hybride;
      return hybride;
    }
    return new LocalTransport(electionId);
  }

  /** Ouvre — ou crée — une élection pour le rôle demandé. */
  async open(electionId: string, role: Role): Promise<void> {
    this.role = role;
    this.origin = originFor(role);
    this.electionId = electionId;
    this.liaison = lireLiaison(electionId);
    this.transport = this.creerTransport(electionId);
    this.events = await this.transport.list();
    this.recompute();
    this.detach?.();
    this.detach = this.transport.subscribe(() => {
      void this.reload();
    });
    this.notify();
  }

  close(): void {
    this.detach?.();
    this.detach = null;
    this.hybride?.arreter();
    this.hybride = null;
    this.liaison = null;
    this.transport = null;
    this.electionId = null;
    this.events = [];
    this.state = emptyState('');
    this.computation = compute(this.state);
    this.notify();
  }

  async reload(): Promise<void> {
    if (!this.transport) return;
    this.events = await this.transport.list();
    this.recompute();
    this.notify();
  }

  /** Crée une élection et renvoie son identifiant. */
  async create(config: ElectionConfig): Promise<string> {
    const electionId = randomId('el');
    const salt = randomId('sel');
    this.role = 'bureau';
    this.origin = originFor('bureau');
    this.electionId = electionId;
    this.liaison = null;
    this.transport = this.creerTransport(electionId);
    this.events = [];
    this.recompute();
    rememberElection({
      id: electionId,
      nom: config.nom,
      classe: config.classe,
      etablissement: config.etablissement,
      dateISO: config.dateISO,
      creeLe: Date.now(),
    });
    await this.dispatch('election.created', { config, salt });
    this.detach?.();
    this.detach = this.transport.subscribe(() => {
      void this.reload();
    });
    this.notify();
    return electionId;
  }

  /**
   * Ajoute un événement au journal.
   *
   * Les écritures sont sérialisées : le compteur d'instance reste monotone
   * même si deux actions sont déclenchées coup sur coup.
   */
  dispatch(type: EventType, payload: unknown): Promise<void> {
    const suite = this.file.then(async () => {
      if (!this.transport || this.electionId === null) {
        throw new Error('Aucune élection ouverte');
      }
      const event = makeEvent({
        electionId: this.electionId,
        type,
        payload,
        origin: this.origin,
        state: this.state,
      });
      await this.transport.append(event);
      this.events = [...this.events, event];
      this.recompute();
      this.notify();
    });
    this.file = suite.catch(() => undefined);
    /*
      L'état public est publié **après** la fin de l'écriture. Le publier depuis
      l'intérieur de `suite` provoquerait un interblocage : la publication
      attendrait sa propre place dans la file sérialisée.
    */
    if (this.hybride !== null && TYPES_PUBLICS.has(type)) {
      return suite.then(() => this.dispatch('public.state', this.etatPublic()));
    }
    return suite;
  }

  /** État public : ce que le relais peut rendre à n'importe qui, sans secret. */
  etatPublic(): EtatPublic {
    const tour: 1 | 2 = this.state.phase === 'TOUR2' ? 2 : 1;
    const computation = this.computation;
    const items =
      tour === 2
        ? computation.items.filter((item) => computation.itemsEnLice2.includes(item.id))
        : computation.items;
    const etat: EtatPublic = {
      phase: this.state.phase,
      salt: this.state.salt,
      items: items.map((item) => ({ id: item.id, label: item.label, kind: item.kind })),
      voixDisponibles: this.votersForRound(tour),
      inscrits: computation.round1.inscrits,
      votants: computation.round1.votants,
      blancs: computation.round1.blancs,
      nuls: computation.round1.nuls,
      nom: this.state.config?.nom,
      classe: this.state.config?.classe,
    };
    return etat;
  }

  get estEnLigne(): boolean {
    return this.liaison !== null;
  }

  /**
   * Publie l'élection sur le relais et bascule sur le transport hybride.
   *
   * À partir de là, les bulletins déposés depuis les téléphones remontent tout
   * seuls dans le journal local, qui reste l'autorité. Renvoie le code de
   * liaison court, celui que l'enseignant projette à la classe.
   */
  async publierEnLigne(): Promise<string> {
    if (this.electionId === null) throw new Error('Aucune élection ouverte');
    const codes = this.state.codes;
    const config = this.state.config;
    if (codes === null) throw new Error('Générez d’abord les codes de vote.');
    if (config === null) throw new Error('Élection incomplète.');
    if (this.liaison !== null) return this.liaison.code;

    const electionId = this.electionId;
    const code = await creerElectionDistant({
      electionId,
      teacherHash: codes.teacherHash,
      journal: this.events,
      meta: {
        nom: config.nom,
        classe: config.classe,
        etablissement: config.etablissement,
        expiresAt: purgeDate(this.state).getTime(),
      },
    });

    this.liaison = { code, teacherHash: codes.teacherHash };
    ecrireLiaison(electionId, this.liaison);
    this.detach?.();
    this.transport = this.creerTransport(electionId);
    this.events = await this.transport.list();
    this.recompute();
    this.detach = this.transport.subscribe(() => {
      void this.reload();
    });
    this.notify();
    await this.dispatch('public.state', this.etatPublic());
    /*
      On pousse explicitement avant de rendre la main : sans état public sur le
      relais, aucun élève ne pourrait voter. Mieux vaut une publication lente
      qu'une publication qu'on croit faite.
    */
    await this.hybride?.synchroniser();
    return code;
  }

  // -------------------------------------------------------------- roster

  async importRoster(persons: Person[]): Promise<void> {
    await this.dispatch('roster.imported', { persons });
  }

  // --------------------------------------------------------------- codes

  codes(): StoredCodes | null {
    return this.electionId ? loadCodes(this.electionId) : null;
  }

  hasCodes(): boolean {
    return this.state.codes !== null;
  }

  /**
   * Génère la planche de codes.
   *
   * Une fois les codes créés, la session en cours reste déverrouillée :
   * la personne qui vient de les générer est nécessairement l'enseignant,
   * il serait absurde de lui redemander aussitôt le code qu'elle découvre.
   */
  async generateCodes(count: number): Promise<StoredCodes> {
    if (this.electionId === null) throw new Error('Aucune élection ouverte');
    const eleves = Array.from({ length: Math.max(1, count) }, () => generateCode(CODE_LENGTHS.eleve));
    const enseignant = generateCode(CODE_LENGTHS.enseignant);
    const stored: StoredCodes = { eleves, enseignant };
    saveCodes(this.electionId, stored);
    /*
      Le marqueur de session est posé **avant** la publication de
      `codes.generated` : le rendu déclenché par cet événement vérifie
      `isUnlocked()`, et le poser après afficherait l'écran de verrouillage au
      professeur qui vient justement de générer les codes.
    */
    this.marquerDeverrouille();
    const salt = this.state.salt;
    await this.dispatch('codes.generated', {
      count: eleves.length,
      hashes: eleves.map((code) => tokenHash(code.raw, salt)),
      teacherHash: tokenHash(enseignant.raw, salt),
    });
    return stored;
  }

  /** Le code enseignant déverrouille le bureau pour la session en cours. */
  unlock(code: string): boolean {
    const attendu = this.state.codes?.teacherHash;
    if (!attendu) return true;
    const fourni = tokenHash(code, this.state.salt);
    if (fourni !== attendu) return false;
    this.marquerDeverrouille();
    this.notify();
    return true;
  }

  private marquerDeverrouille(): void {
    sessionStorage.setItem(`${UNLOCK_KEY}/${this.electionId ?? ''}`, '1');
  }

  isUnlocked(): boolean {
    if (!this.state.codes) return true;
    return sessionStorage.getItem(`${UNLOCK_KEY}/${this.electionId ?? ''}`) === '1';
  }

  // --------------------------------------------------------- candidatures

  async declare(titulaireId: string, suppleantId: string | null): Promise<void> {
    await this.dispatch(
      'candidate.declared',
      suppleantId ? { titulaireId, suppleantId } : { titulaireId },
    );
  }

  async removeItem(itemId: string): Promise<void> {
    await this.dispatch('candidate.removed', { itemId });
  }

  // ---------------------------------------------------------------- phases

  async setPhase(to: Phase): Promise<void> {
    await this.dispatch('phase.changed', { from: this.state.phase, to });
  }

  async decideTiebreak(round: 1 | 2, tiedItemIds: string[], chosenItemId: string, motif: string): Promise<void> {
    await this.dispatch('tiebreak.decided', { round, tiedItemIds, chosenItemId, motif, ts: Date.now() });
  }

  async decideMandate(personId: string, accepted: boolean, motif: string): Promise<void> {
    await this.dispatch('mandate.decided', { personId, accepted, motif, ts: Date.now() });
  }

  async designate(titulaireId: string, suppleantId: string, motif: string): Promise<void> {
    await this.dispatch('suppleant.designated', { titulaireId, suppleantId, motif, ts: Date.now() });
  }

  // ------------------------------------------------- décisions souveraines

  async validateResult(motif: string): Promise<void> {
    await this.dispatch('result.validated', {
      seats: [],
      motif,
      computedFingerprint: this.computation.empreinte,
      phase: this.state.phase,
    });
  }

  async overrideResult(seats: Seat[], motif: string): Promise<void> {
    await this.dispatch('result.overridden', {
      seats,
      motif,
      computedFingerprint: this.computation.empreinte,
      phase: this.state.phase,
    });
  }

  // --------------------------------------------------------------- isoloir

  votersForRound(round: 1 | 2): number {
    if (round === 1) return this.state.config?.rules.voixTour1 ?? DEFAULT_RULES.voixTour1;
    return Math.max(0, (this.state.config?.rules.titulaires ?? DEFAULT_RULES.titulaires) - this.computation.elusTour1.length);
  }

  /** Vérifie un code élève et dépose le bulletin. */
  async castBallot(
    rawCode: string,
    choices: string[],
    blank: boolean,
    round: 1 | 2,
  ): Promise<'ok' | 'inconnu' | 'double'> {
    const hash = tokenHash(rawCode, this.state.salt);
    const connus = this.state.codes?.hashes ?? [];
    if (!connus.includes(hash)) return 'inconnu';
    if (this.state.ballots.some((ballot) => ballot.tokenHash === hash && ballot.round === round)) {
      return 'double';
    }
    await this.dispatch('ballot.cast', { tokenHash: hash, choices, blank, round });
    return 'ok';
  }

  codeIsKnown(rawCode: string): boolean {
    const hash = tokenHash(rawCode, this.state.salt);
    return (this.state.codes?.hashes ?? []).includes(hash);
  }

  // ---------------------------------------------------------------- purge

  /**
   * Supprime définitivement l'élection **sur cet appareil** (D13).
   *
   * Tout ce qui la concerne disparaît : journal, planche de codes, entrée
   * d'index, liaison au relais et marqueur de déverrouillage. Sans le retrait
   * de la liaison, rouvrir l'ancienne adresse rapatrierait le journal depuis
   * le relais et ferait réapparaître l'élection — « tout effacer » ne doit pas
   * pouvoir être annulé par un rechargement.
   *
   * La copie publiée sur le relais, elle, suit sa propre expiration (J+7) :
   * cet effacement-là ne concerne que l'appareil.
   */
  async purge(): Promise<void> {
    if (this.electionId === null) return;
    const electionId = this.electionId;
    purgeElection(electionId);
    supprimerLiaison(electionId);
    localStorage.removeItem(`${SAUVEGARDE_KEY}/${electionId}`);
    sessionStorage.removeItem(`${UNLOCK_KEY}/${electionId}`);
    this.transport = null;
    this.liaison = null;
    this.electionId = null;
    this.events = [];
    this.recompute();
    this.notify();
  }

  // ------------------------------------------------------------ sauvegarde

  /** Date de la dernière sauvegarde exportée pour cette élection, ou `null`. */
  get derniereSauvegarde(): number | null {
    if (this.electionId === null) return null;
    const brut = localStorage.getItem(`${SAUVEGARDE_KEY}/${this.electionId}`);
    if (brut === null) return null;
    const valeur = Number(brut);
    return Number.isFinite(valeur) ? valeur : null;
  }

  /**
   * Exporte une sauvegarde complète, en texte JSON.
   * Le fichier contient les codes : il se conserve comme le code enseignant.
   */
  exporterSauvegarde(): string {
    if (this.electionId === null) throw new Error('Aucune élection ouverte');
    const sauvegarde: Sauvegarde = {
      format: 'election.nsi.xyz/sauvegarde',
      version: 1,
      exporteLe: Date.now(),
      electionId: this.electionId,
      journal: this.events,
      codes: this.codes(),
    };
    localStorage.setItem(`${SAUVEGARDE_KEY}/${this.electionId}`, String(sauvegarde.exporteLe));
    this.notify();
    return JSON.stringify(sauvegarde, null, 2);
  }

  /**
   * Restaure une sauvegarde. Le journal est **vérifié** avant toute écriture :
   * une chaîne d'empreintes rompue est refusée, jamais restaurée à l'aveugle.
   *
   * `deverrouiller` n'est utilisé que pour l'élection **fictive** embarquée :
   * sa planche de codes est publique par construction, redemander le code à
   * l'ouverture n'apporterait rien. La restauration d'une vraie sauvegarde
   * reste, elle, verrouillée.
   */
  async importerSauvegarde(texte: string, options: { deverrouiller?: boolean } = {}): Promise<string> {
    let donnees: Sauvegarde;
    try {
      donnees = JSON.parse(texte) as Sauvegarde;
    } catch {
      throw new Error('Fichier illisible : ce n’est pas du JSON.');
    }
    if (donnees === null || typeof donnees !== 'object' || donnees.format !== 'election.nsi.xyz/sauvegarde') {
      throw new Error('Ce fichier n’est pas une sauvegarde d’élection.');
    }
    if (donnees.version !== 1) {
      throw new Error(`Version de sauvegarde inconnue : ${String(donnees.version)}.`);
    }
    if (!Array.isArray(donnees.journal) || donnees.journal.length === 0) {
      throw new Error('La sauvegarde ne contient aucun événement.');
    }
    const identifiants = new Set(donnees.journal.map((evenement) => evenement.electionId));
    if (identifiants.size !== 1 || !identifiants.has(donnees.electionId)) {
      throw new Error('Journal incohérent : plusieurs élections s’y mélangent.');
    }
    const chaine = verifyChain(donnees.journal);
    if (!chaine.ok) {
      throw new Error(`Journal altéré, restauration refusée : ${chaine.reason ?? 'chaîne rompue'}.`);
    }

    const transport = new LocalTransport(donnees.electionId);
    await transport.restore(donnees.journal, donnees.codes);

    const restaure = project(donnees.journal, donnees.electionId);
    if (restaure.config !== null) {
      rememberElection({
        id: donnees.electionId,
        nom: restaure.config.nom,
        classe: restaure.config.classe,
        etablissement: restaure.config.etablissement,
        dateISO: restaure.config.dateISO,
        creeLe: donnees.journal[0]?.ts ?? Date.now(),
      });
    }
    await this.open(donnees.electionId, 'bureau');
    if (options.deverrouiller === true) this.marquerDeverrouille();
    return donnees.electionId;
  }

  /** Photographie de l'état de préparation, pour un feu vert avant le jour J. */
  diagnostic(): Diagnostic {
    let stockageOk = true;
    try {
      const cle = 'election.nsi.xyz/v1/essai-stockage';
      localStorage.setItem(cle, '1');
      if (localStorage.getItem(cle) !== '1') stockageOk = false;
      localStorage.removeItem(cle);
    } catch {
      stockageOk = false;
    }
    const chaine = verifyChain(this.events);
    return {
      stockageOk,
      evenements: this.events.length,
      chaineOk: chaine.ok,
      chaineDetail: chaine.reason,
      codes: this.state.codes?.count ?? 0,
      eleves: this.state.persons.length,
      bulletins: this.state.ballots.length,
      purgeLe: this.state.config === null ? null : purgeDate(this.state),
      derniereSauvegarde: this.derniereSauvegarde,
    };
  }

  // ---------------------------------------------------------------- thème

  setTheme(theme: ThemeChoice): void {
    this.theme = theme;
    localStorage.setItem(THEME_KEY, theme);
    if (theme === 'auto') document.documentElement.removeAttribute('data-theme');
    else document.documentElement.setAttribute('data-theme', theme);
    this.notify();
  }

  cycleTheme(): void {
    this.setTheme(this.theme === 'auto' ? 'light' : this.theme === 'light' ? 'dark' : 'auto');
  }
}

export const store = new Store();
